import { useCallback, useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { AdminSupabase } from './supabase';

export type SessionState =
  | { kind: 'loading' }
  | { kind: 'signedOut'; error?: string }
  | { kind: 'checking'; email: string }
  | { kind: 'admin'; email: string }
  | { kind: 'notAdmin'; email: string }
  | { kind: 'error'; email: string; message: string };

export interface AdminSession {
  state: SessionState;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-run the `is_admin` check after it failed. */
  retry: () => void;
}

function emailOf(session: Session): string {
  return session.user.email ?? session.user.user_metadata?.email ?? 'this account';
}

/**
 * Tracks the Supabase session (Google OAuth, persisted by supabase-js in the browser) and, once
 * signed in, asks the database `is_admin()`. Only `admin` unlocks the app; the `admin-decks`
 * function checks again on every call.
 */
export function useAdminSession(supabase: AdminSupabase): AdminSession {
  const [state, setState] = useState<SessionState>({ kind: 'loading' });
  const checkedUser = useRef<string | null>(null);
  const sessionRef = useRef<Session | null>(null);

  const check = useCallback(
    async (session: Session) => {
      const email = emailOf(session);
      setState({ kind: 'checking', email });
      try {
        const { data, error } = await supabase.rpc('is_admin');
        if (sessionRef.current?.user.id !== session.user.id) return; // signed out/changed meanwhile
        if (error) {
          checkedUser.current = null;
          setState({ kind: 'error', email, message: error.message });
          return;
        }
        setState(data === true ? { kind: 'admin', email } : { kind: 'notAdmin', email });
      } catch (e) {
        checkedUser.current = null;
        setState({
          kind: 'error',
          email,
          message: e instanceof Error ? e.message : 'Could not check admin access.',
        });
      }
    },
    [supabase]
  );

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      sessionRef.current = session;
      if (!session) {
        checkedUser.current = null;
        setState({ kind: 'signedOut' });
        return;
      }
      if (event === 'TOKEN_REFRESHED') return;
      // Same user again (INITIAL_SESSION followed by SIGNED_IN): already checked.
      if (checkedUser.current === session.user.id) return;
      checkedUser.current = session.user.id;
      // supabase-js must not be called from inside this callback; run the check after it returns.
      setTimeout(() => void check(session), 0);
    });
    return () => data.subscription.unsubscribe();
  }, [supabase, check]);

  const signIn = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) setState({ kind: 'signedOut', error: error.message });
  }, [supabase]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    checkedUser.current = null;
    sessionRef.current = null;
    setState({ kind: 'signedOut' });
  }, [supabase]);

  const retry = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    checkedUser.current = session.user.id;
    void check(session);
  }, [check]);

  return { state, signIn, signOut, retry };
}
